
import { supabase } from '../supabase';
import { fetchAll } from './fetchAll';
import { Invoice, OrderBundle, User, CompanySettings, Product } from '../../types';
import { aggregateProducts, shortOrderNumber } from '../orderLogic';
import { breakdownKr, breakdownRowText, discountRuleText } from '../bundles';
import { deliveryVatRate, invoiceVat, invoiceVatLines, priceExVat } from '../invoiceVat';
import { bonusLevel, itemCommissionOf, levelRate } from '../bonus';
import { formatOre, stockholmDate } from '../vat';
import type { ProductCategory } from '../vat';
import { FinancialSummary } from '../../types';

const roundOre = (value: number) => Math.round(value * 100) / 100;

/**
 * Skapar fakturan och låser ordrarna i EN databastransaktion
 * (create_invoice_secure): löpande fakturanummer från en sekvens, kontroll att
 * ingen order redan är fakturerad och att summan stämmer med ordrarna.
 */
export const createInvoice = async (
    bundle: OrderBundle,
    recipient: User,
    financials: FinancialSummary,
    companySettings: CompanySettings,
    products: Product[],
    /** Dagen varorna levereras till gruppen. Avgör momssatsen. */
    deliveryDate: string,
    categories: ProductCategory[]
): Promise<Invoice> => {
    // Föreningens konto behövs när fakturan blir en avräkning (CoffeeNova betalar ut).
    const { data: cls } = await supabase.from('classes').select('payout_account').eq('id', bundle.classId).maybeSingle();
    const recipientSnapshot = {
        name: recipient.name,
        email: recipient.email,
        address: bundle.deliveryAddress || '',
        payoutAccount: cls?.payout_account || undefined
    };

    // Momssatsen som gäller på leveransdagen (beskattningsgrundande händelse), svensk tid.
    const rateOf = deliveryVatRate(products, categories, new Date(`${deliveryDate}T12:00:00Z`));
    const pickupOrders = bundle.orders.filter(o => o.paymentMethod !== 'STRIPE');
    const onlineOrders = bundle.orders.filter(o => o.paymentMethod === 'STRIPE');
    const vat = invoiceVat(bundle.orders, itemCommissionOf(financials.bonus), financials.totalClassProfit, rateOf);
    const multiRate = vat.rows.length > 1;

    // Fullständig faktura: varorna gruppen köpt (hämtade ordrar) med mängd,
    // enhetspris exklusive moms och sats, rabatten per sats och provisionen
    // för köp betalda i kassan (CoffeeNova har redan sålt de varorna till kunden).
    const productLines = aggregateProducts(pickupOrders, products, rateOf).map(line => ({
        kind: 'product' as const,
        description: line.variantName ? `${line.name} – ${line.variantName}` : line.name,
        vatRate: line.vatRate,
        quantity: line.totalQty,
        unitPrice: roundOre(line.price),
        unitPriceExVat: priceExVat(line.price, line.vatRate),
        total: roundOre(line.revenue),
        // Paket: vad ett paket innehåller och hur priset fördelas, så att momsen går att följa.
        ...(line.parts ? {
            details: [
                ...line.parts.map(r => `Per paket: ${breakdownRowText(r)}`),
                // Samma rabattrad på båda satsernas rader: klassens version slår ihop dem till en.
                line.bundleDiscount ? `Paketrabatt −${breakdownKr(line.bundleDiscount)} per paket, ${discountRuleText(!!line.discountOnHighestRate)}` : '',
                line.mixedBundle ? 'Paketets övriga delar har annan momssats och står på egen rad.' : '',
            ].filter(Boolean)
        } : {})
    }));
    const linesSnapshot = [
        ...productLines,
        ...vat.rows.filter(r => r.discount !== 0).map(r => ({
            kind: 'discount' as const,
            // "Rabatt", inte "provision": rabatten minskar momsen, provisionen för kassaköp gör det inte.
            // Klassens version visar raderna som en: "Gruppens provision enligt bonustrappan".
            description: `Rabatt enligt bonustrappan${multiRate ? `, ${r.rate} %-varor` : ''}`,
            vatRate: r.rate,
            quantity: 1,
            unitPrice: -r.discount,
            unitPriceExVat: -priceExVat(r.discount, r.rate),
            total: -r.discount
        })),
        ...(vat.onlineProvision !== 0 ? [{
            kind: 'provision' as const,
            description: `Provision: gruppens del av ${onlineOrders.length} köp betalda i kassan (ingen moms)`,
            vatRate: null,
            quantity: 1,
            unitPrice: -vat.onlineProvision,
            total: -vat.onlineProvision,
            // Vilka köp provisionen gäller, så att den ansvariga kan stämma av. Varorna
            // är redan sålda och kvitterade till kunden, så de står inte som varurader.
            details: onlineOrders.map(o => [
                shortOrderNumber(o.id),
                stockholmDate(new Date(o.paidAt || o.timestamp)),
                o.items.map(i => `${i.quantity > 1 ? `${i.quantity} × ` : ''}${i.productNameSnapshot || 'Produkt'}${i.variantName ? ` – ${i.variantName}` : ''}`).join(', '),
                `${formatOre(o.totalAmount)} kr${o.deliveryMethod === 'HOME' ? ' (hemleverans)' : ''}`,
            ].join(' · '))
        }] : [])
    ];

    const toPay = roundOre(financials.toPay);
    const vatBreakdown = invoiceVatLines(vat.rows);
    const vatAmount = roundOre(vatBreakdown.reduce((s, l) => s + l.vat, 0));
    // Netto = momspliktigt belopp utan moms (bara hämtade varor). Provisionen på
    // köp betalda i kassan är utan moms och står på en egen rad:
    //   att betala = netto + moms − provision för kassaköp
    const netAmount = roundOre(vatBreakdown.reduce((s, l) => s + l.net, 0));

    const financialSnapshot = {
        totalSales: roundOre(financials.totalSales),
        collectedBySellers: roundOre(financials.collectedBySellers),
        paidOnline: roundOre(financials.paidOnline),
        classProfit: roundOre(financials.totalClassProfit),
        onlineProvision: vat.onlineProvision,
        toPay,
        vatAmount,
        netAmount,
        vatBreakdown,
        deliveryDate,
        // Bonustrappan för fakturan. Databasen räknar om provisionen och stoppar fakturan om den inte stämmer.
        bonus: {
            priorRegularSales: roundOre(financials.priorRegularSales),
            regularSales: financials.bonus.totals.regularSales,
            regularCommission: financials.bonus.regularCommission,
            fixedSales: financials.bonus.totals.fixedSales,
            fixedCommission: financials.bonus.fixedCommission,
            levelRate: levelRate(bonusLevel(financials.priorRegularSales + financials.bonus.totals.regularSales)),
        }
    };

    const { data, error } = await supabase.rpc('create_invoice_secure', {
        p_class_id: bundle.classId,
        p_order_ids: bundle.orders.map(o => o.id),
        p_recipient: recipientSnapshot,
        p_sender: companySettings,
        p_lines: linesSnapshot,
        p_financial: financialSnapshot
    });

    if (error) throw new Error(error.message);
    return mapInvoice(data);
};

export const markInvoiceSent = async (invoiceId: string) => {
    const { error } = await supabase.from('invoices').update({
        status: 'SENT',
        sent_at: new Date().toISOString()
    }).eq('id', invoiceId);
    
    if (error) throw error;
};

export const updateInvoiceStatus = async (invoiceId: string, status: 'DRAFT' | 'SENT' | 'PAID') => {
    const { error } = await supabase.from('invoices').update({
        status: status
    }).eq('id', invoiceId);
    
    if (error) throw error;
};

export const getInvoiceByClass = async (classId: string): Promise<Invoice | null> => {
    const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('class_id', classId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

    if (error || !data) return null;

    return mapInvoice(data);
};

export const getAllInvoices = async (): Promise<Invoice[]> => {
    // Sida för sida: varje grupp får minst en faktura per säljperiod.
    const data = await fetchAll<any>((from, to) => supabase
        .from('invoices')
        .select('*')
        .order('created_at', { ascending: false })
        .order('id')
        .range(from, to));
    return data.map(mapInvoice);
};

// Helper mapper
const mapInvoice = (row: any): Invoice => ({
    id: row.id,
    invoiceNumber: row.invoice_number,
    classId: row.class_id,
    recipientSnapshot: row.recipient_snapshot,
    senderSnapshot: row.sender_snapshot || undefined, // Map the new column
    linesSnapshot: row.lines_snapshot,
    financialSnapshot: row.financial_snapshot,
    status: row.status,
    createdAt: new Date(row.created_at).getTime(),
    dueDate: new Date(row.due_date).getTime(),
    sentAt: row.sent_at ? new Date(row.sent_at).getTime() : undefined,
    // Finns efter migreringen invoice_paid_at; tidigare saknas kolumnen och värdet blir tomt.
    paidAt: row.paid_at ? new Date(row.paid_at).getTime() : undefined
});
